import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { AdminHead } from "./AdminLayout";

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [pw, setPw] = useState({ current: "", new: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch((e) => setErr(errMsg(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setMsg("");
    setErr("");
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      setMsg("Settings saved.");
    } catch (e2) {
      setErr(errMsg(e2));
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwMsg("");
    setPwErr("");
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current: "", new: "", confirm: "" });
      setPwMsg("Password updated.");
    } catch (e2) {
      setPwErr(errMsg(e2));
    }
  };

  if (!s) return <p className="mono">Loading…</p>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  return (
    <div data-testid="admin-settings">
      <AdminHead title="Settings" sub="Contact routing, public copy and your password." />

      <form className="card" style={{ maxWidth: 620, marginBottom: 40 }} onSubmit={save} data-testid="settings-form">
        <label className="field">
          <span>CC recipients — studio addresses copied on every inquiry (comma separated)</span>
          <input
            className="inp"
            value={s.recipientEmail || ""}
            onChange={set("recipientEmail")}
            placeholder="ari@balboarecordingstudio.com, danny@balboarecordingstudio.com"
            data-testid="settings-recipient"
          />
        </label>
        <label className="field">
          <span>Mailgun sending domain</span>
          <input
            className="inp"
            value={s.mailgunDomain || ""}
            onChange={set("mailgunDomain")}
            placeholder="mg.balboarecordingstudio.com"
            data-testid="settings-mailgun-domain"
          />
        </label>
        <label className="field">
          <span>Instagram handle</span>
          <input className="inp" value={s.igHandle || ""} onChange={set("igHandle")} data-testid="settings-ig" />
        </label>
        <label className="field">
          <span>Rates block (shown on Contact page)</span>
          <textarea className="inp" value={s.rates || ""} onChange={set("rates")} data-testid="settings-rates" />
        </label>
        <label className="field" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="checkbox"
            checked={!!s.bannerOn}
            onChange={(e) => setS({ ...s, bannerOn: e.target.checked })}
            data-testid="settings-banner-toggle"
          />
          <span style={{ margin: 0 }}>Show availability banner</span>
        </label>
        <label className="field">
          <span>Availability banner text</span>
          <input className="inp" value={s.bannerText || ""} onChange={set("bannerText")} data-testid="settings-banner-text" />
        </label>
        <label className="field" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="checkbox"
            checked={!!s.halftoneOn}
            onChange={(e) => setS({ ...s, halftoneOn: e.target.checked })}
            data-testid="settings-halftone-toggle"
          />
          <span style={{ margin: 0 }}>Halftone print effect on photos (off = full colour photos)</span>
        </label>
        <label className="field">
          <span>Apple Music playlist URL</span>
          <input className="inp" value={s.appleMusicUrl || ""} onChange={set("appleMusicUrl")} data-testid="settings-apple-music" />
        </label>
        <label className="field">
          <span>Notable clients (separate with " | ")</span>
          <textarea
            className="inp"
            value={s.notableClients || ""}
            onChange={set("notableClients")}
            data-testid="settings-notable-clients"
          />
        </label>
        {msg && <p className="mono" style={{ fontSize: 13 }} data-testid="settings-saved">{msg}</p>}
        {err && <p className="mono" style={{ fontSize: 13, color: "var(--orange)" }}>{err}</p>}
        <button className="btn btn-sm" type="submit" data-testid="settings-save">Save Settings</button>
      </form>

      <form className="card" style={{ maxWidth: 620, marginBottom: 40 }} onSubmit={save} data-testid="copy-form">
        <p className="label" style={{ marginBottom: 16 }}>Page copy — edit the words on the site</p>
        <label className="field">
          <span>Home — hero paragraph (under the headline)</span>
          <textarea
            className="inp"
            rows={4}
            value={s.homeHeroSub || ""}
            onChange={set("homeHeroSub")}
            data-testid="settings-home-hero-sub"
          />
        </label>
        <label className="field">
          <span>Home — "Two rooms, one block apart" paragraph</span>
          <textarea
            className="inp"
            rows={4}
            value={s.homeRoomsBody || ""}
            onChange={set("homeRoomsBody")}
            data-testid="settings-home-rooms-body"
          />
        </label>
        <label className="field">
          <span>Studio page — intro paragraph under "The Rooms"</span>
          <textarea
            className="inp"
            rows={3}
            value={s.studioIntro || ""}
            onChange={set("studioIntro")}
            data-testid="settings-studio-intro"
          />
        </label>
        <label className="field">
          <span>Studio page — Balboa Recording Studio description (blank line = new paragraph)</span>
          <textarea
            className="inp"
            rows={7}
            value={s.balboaBody || ""}
            onChange={set("balboaBody")}
            data-testid="settings-balboa-body"
          />
        </label>
        <label className="field">
          <span>Studio page — Balboa Bungalow description (blank line = new paragraph)</span>
          <textarea
            className="inp"
            rows={7}
            value={s.bungalowBody || ""}
            onChange={set("bungalowBody")}
            data-testid="settings-bungalow-body"
          />
        </label>
        {msg && <p className="mono" style={{ fontSize: 13 }} data-testid="copy-saved">{msg}</p>}
        <button className="btn btn-sm" type="submit" data-testid="copy-save">Save Copy</button>
      </form>

      <form className="card" style={{ maxWidth: 620 }} onSubmit={changePw} data-testid="password-form">
        <p className="label" style={{ marginBottom: 16 }}>Change password</p>
        <label className="field">
          <span>Current password</span>
          <input
            className="inp"
            type="password"
            value={pw.current}
            onChange={(e) => setPw({ ...pw, current: e.target.value })}
            data-testid="pw-current"
          />
        </label>
        <label className="field">
          <span>New password</span>
          <input
            className="inp"
            type="password"
            value={pw.new}
            onChange={(e) => setPw({ ...pw, new: e.target.value })}
            data-testid="pw-new"
          />
        </label>
        <label className="field">
          <span>Confirm new password</span>
          <input
            className="inp"
            type="password"
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            data-testid="pw-confirm"
          />
        </label>
        {pwMsg && <p className="mono" style={{ fontSize: 13 }} data-testid="pw-success">{pwMsg}</p>}
        {pwErr && <p className="mono" style={{ fontSize: 13, color: "var(--orange)" }} data-testid="pw-error">{pwErr}</p>}
        <button className="btn btn-sm" type="submit" data-testid="pw-save">Update Password</button>
      </form>
    </div>
  );
}
